// fetchMarketBackdrop.js
// DAS-2832: the Broad Picture bits an ALERT needs — the risk watchlist (so a saved
// match can be shown with its direction, tier and reason), the tailwinds/headwinds
// (to name the wind on the alert's sector), and the growth + inflation readings.
//
// Only growth and inflation are exposed: an alert is roughly a six-month decision,
// and those two are the readings that stay valid for weeks-to-months. Volatility
// (days) and commodity/geopolitics (days-weeks) move faster than the decision does,
// so putting them next to an alert would invite reading a day-trade signal into a
// six-month call (DAS-2829).
//
// Memoized with a short TTL: an alert list renders many cards and they all want the
// same market-wide answer. Never throws — a failure just hides the block.
import api from './api';

const TTL_MS = 120000; // 2 min, same as fetchBroadPictureText
let _cache = { ts: 0, data: null };
let _inflight = null;

export const BACKDROP_AXES = ['growth', 'inflation'];

async function _load() {
  const out = { axes: {}, tailwinds: [], headwinds: [], risks: {}, epoch: null };
  const [bp, scen] = await Promise.allSettled([
    api.get('/broad_picture'),
    api.get('/scenarios'),
  ]);

  if (bp.status === 'fulfilled') {
    const latest = bp.value?.data?.latest || null;
    if (latest) {
      out.epoch = latest.epoch || null;
      const axes = latest.axes_json || {};
      BACKDROP_AXES.forEach((ax) => { if (axes[ax]) out.axes[ax] = axes[ax]; });
      const impl = latest.implications_json || {};
      out.tailwinds = impl.tailwinds || [];
      out.headwinds = impl.headwinds || [];
    }
  }

  if (scen.status === 'fulfilled') {
    // One lookup keyed by slug: registry row (name, description) merged with the
    // latest run's score (direction, tier, why, sources).
    const scores = {};
    (scen.value?.data?.latest_scores || []).forEach((s) => { if (s?.slug) scores[s.slug] = s; });
    (scen.value?.data?.registry || []).forEach((r) => {
      if (r?.slug) out.risks[r.slug] = { ...r, ...(scores[r.slug] || {}) };
    });
  }

  return out;
}

export async function fetchMarketBackdrop() {
  if (_cache.ts && Date.now() - _cache.ts < TTL_MS) return _cache.data;
  if (_inflight) return _inflight;          // many cards mount at once — one request
  _inflight = _load()
    .then((data) => { _cache = { ts: Date.now(), data }; return data; })
    .catch(() => _cache.data)               // keep whatever we had; never throw
    .finally(() => { _inflight = null; });
  return _inflight;
}

/**
 * The tailwind/headwind on a given area, matched the same way the scoring does
 * (DR_Broad_Picture classifies each wind as sector/region + a canonical name).
 * Returns { kind, area, horizon } or null.
 */
export function windForArea(backdrop, matchType, matchName) {
  if (!backdrop || !matchName) return null;
  const pairs = [['tailwind', backdrop.tailwinds], ['headwind', backdrop.headwinds]];
  for (const [kind, list] of pairs) {
    for (const w of list || []) {
      if (w && w.match_type === matchType && w.match_name === matchName) {
        return { kind, area: w.area || matchName, horizon: w.horizon || null };
      }
    }
  }
  return null;
}
